import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Pagination, Popconfirm, Spin, Tabs, Tooltip, message } from 'antd';
import { ReactComponent as ExpandIcon } from "../../../assets/svgs/icons/expand-icon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/svgs/icons/close-icon.svg";
import { ReactComponent as ShrinkIcon } from "../../../assets/svgs/icons/shrink-icon.svg";
import { ReactComponent as ExportIcon } from "../../../assets/svgs/icons/export-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/svgs/icons/delete-icon.svg";
import { ReactComponent as ReloadIcon } from "../../../assets/svgs/icons/reload-icon.svg";
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { DataAOI } from '../../../types/interfaces';
import { AOIIndexActionType } from '../../../common/constant';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import { updateZValueForAOI, zoomTOAOI } from '../../../common/potreeUtils';
import { getAOIDataFromH3Index, getIntersectedDataWithAOIUsingBBox, processAOIDataFromLambda } from '../../tables/dataIndexTable/AoisTable/AoiUtils';
import { aoiIndexAdd, aoiIndexDelete, aoiIndexList } from '../../tables/dataIndexTable/AoisTable/AoisLambdaUtils';
import { AoisLasExport } from '../../tables/dataIndexTable/AoisTable/AoisLasExport';
import { AoisProcessingMenu } from '../../tables/dataIndexTable/AoisTable/AoisProcessingMenu';
import { DataIndexMenuTableContainer } from '../DataManager.style';
import { ReloadOutlined } from '@ant-design/icons';
import { convertPointsArrToWkt } from '../../../common/gisUtils';

const DefaultValue = 0;

interface AoisMenuTableProps {
    visible: boolean;
    setActiveMenu: (menuName: string | null) => void;
    menuAoisOpen: boolean
    setMenuAoisOpen: React.Dispatch<React.SetStateAction<boolean>>;
}


const AoisMenuTable = ({ visible, setActiveMenu, menuAoisOpen, setMenuAoisOpen }: AoisMenuTableProps) => {
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [tableData, setTableData] = useState<DataAOI[]>([]);
    const [expand, setExpand] = useState<boolean>(false);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<object | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [activeRecord, setActiveRecord] = useState<DataAOI | null>(null);
    const [selectedAoi, setSelectedAoi] = useState<DataAOI[]>([]);
    const [menuModalVisible, setMenuModalVisible] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const tableRef = useRef(null);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const columnsData = [
        { title: "", key: "show", size: 35 },
        { title: "Name", key: "name", size: 250, },
        { title: "EPSG", key: "epsg", size: 250 },
        { title: "Area", key: "areaText", size: 180 },
        { title: "Action", key: "operation", size: 180, }
    ];

    const onReload = async () => {
        setDataLoading(true)
        aoiIndexList(null).then((res: any) => {
            const data = JSON.parse(res)
            window.potreeViewer.scene.removeAllMeasurements();
            setLastEvaluatedKey(data.last_evaluated_key);
            const newUpdatedData = processAOIDataFromLambda(data.data)
            setTableData(newUpdatedData)
        }).catch((err) => {
            console.log(err);
            message.error("Failed to load AOIs")
        }).finally(() => {
            setDataLoading(false)
        });
    }
    useCustomEventListener("saveAoi", async (data: DataAOI) => {
        
        let lastMeasurements = data.measurements.at(-1);
        if (!lastMeasurements) return

        let points = lastMeasurements.points;
        let wktPolygon = convertPointsArrToWkt(points);

        const dataIds = window.potreeViewer.scene.pointclouds.map((pCloud: any) => pCloud.record.data_id)
        const intersectedData = getIntersectedDataWithAOIUsingBBox(dataIds, points, data)
        aoiIndexAdd(wktPolygon, data.name, intersectedData)
            .then((res: any) => {
                const dataRes = JSON.parse(res);

                // Add H3 index to Potree measurement for later use
                let potreeMeasurements = window.potreeViewer.scene.measurements;
                let measure = potreeMeasurements.find((m: any) => {
                    return m.uuid === lastMeasurements.uuid;
                });
                let name = data.name || dataRes.h3_index
                measure.userData.h3_index = dataRes.h3_index;
                measure.userData.name = name;
                data.h3_index = dataRes.h3_index;

                // Add new AOI to table
                let newTableData: any = [...tableData];
                newTableData.push({
                    "area": measure.getArea(),
                    "areaText": measure.areaLabel.text,
                    "epsg": data.epsg,
                    "name": name,
                    "visible": true,
                    "polygon": wktPolygon,
                    "webMercatorPolygon": dataRes.web_mercator_polygon,
                    "h3_index": dataRes.h3_index || ""
                });
                setTableData(newTableData);
                emitCustomEvent("aoiSaved", {
                    success: true,
                })
                setActiveMenu("aois")
            })
            .catch((err) => {
                console.log(err);
                emitCustomEvent("aoiSaved",{
                    success : false,
                    error : err
                })
            });
    });
    useCustomEventListener("DataIndexTableChanged", (dataIndexTable) => {
        const activeProjection = window.activeProjection?.getCRS()

        if (!activeProjection) return

        // Check AOI projection with active projection
        if (tableData.length > 0) {
            let aoi = tableData[0]
            if (aoi.epsg === activeProjection) {
                console.log("AOI projection matches active projection")
                return
            }
        }

        aoiIndexList(lastEvaluatedKey).then((res: any) => {
            const data = JSON.parse(res)
            // Remove all measurements existing in Potree before adding new ones
            setLastEvaluatedKey({ ...data.last_evaluated_key });

            window.potreeViewer.scene.removeAllMeasurements();
            const newUpdatedData = processAOIDataFromLambda(data.data)
            setTableData(newUpdatedData);
        }).catch((err) => {
            console.log(err);
            message.error("Failed to load AOIs")
        });
    });

    useEffect(() => {
        if(menuAoisOpen){
            setActiveRecord(null)
            setMenuModalVisible(true)
        }
    }, [menuAoisOpen]);

    const handleDeleteAoi = () => {
        setDeleteLoading(true);

        const h3_indexes = selectedAoi.map(aoi => aoi.h3_index);
        // Collect all h3_index values
        aoiIndexDelete(h3_indexes).then((res) => {
            // Remove Potree measurements from scene using H3 indices
            let measurements = window.potreeViewer.scene.measurements;
            h3_indexes.forEach(h3_index => {
                let measure = measurements.find((m: any) => {
                    return m.userData.h3_index === h3_index;
                });
                if (measure) {
                    window.potreeViewer.scene.removeMeasurement(measure);
                }
            });

            // Remove AOIs from table
            let newTableData = tableData.filter((item: any) => {
                return !h3_indexes.includes(item.h3_index);
            });
            setTableData(newTableData);
            message.success("AOIs deleted successfully");
        }).catch((err) => {
            message.error("Failed to delete AOIs");
            setDeleteLoading(false);
            console.log(err);
        }).finally(() => {
            setDeleteLoading(false);
        });
    };

    const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
        if (tableData.length === 0) {
            return [];
        }
        return columnsData.map(({ title, key, size }) => {
            if (key === "name") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataAOI = row.original;
                        return (
                            <div className='name_btn'>
                                <button
                                    className={`button-name`}
                                    type="button"
                                    // disabled={disabled}
                                    onClick={() => onActionHandler(AOIIndexActionType.ZoomTo, DefaultValue, record)}
                                >
                                    {record.name}
                                </button>
                            </div>
                        );
                    },
                };
            } else if (key === "show") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataAOI = row.original;
                        const showConfirm = (e: any) => {
                            setSelectedAoi((prev) => {
                                const isChecked = e.target.checked;
                                if (!isChecked) {
                                    return prev.filter((item) => item.h3_index !== record.h3_index);
                                } else {
                                    if (prev.find((item) => item.h3_index === record.h3_index)) {
                                        return prev;
                                    } else {
                                        return [...prev, record];
                                    }
                                }
                            });

                            onActionHandler(
                                record.visible ? AOIIndexActionType.Hide : AOIIndexActionType.Show,
                                DefaultValue,
                                record
                            );
                        };

                        return (
                            <div style={{ width: size }} className="table_checkbox_section">
                                <Checkbox onChange={showConfirm} checked={record.visible} />
                            </div>
                        );
                    }
                }
            } else if (key === "operation") {
                return {
                    accessorKey: key,
                    enableSorting: false,
                    header: title || "",
                    size: size,
                    Cell: ({ row }) => {
                        const record: DataAOI = row.original;
                        const handleDelete = () => {
                            let h3_index = record.h3_index;
                            setDataLoading(true);
                            aoiIndexDelete(h3_index).then((res) => {
                                // Remove Potree measurement from scene using H3 index
                                let measurements = window.potreeViewer.scene.measurements;
                                let measure = measurements.find((m: any) => {
                                    return m.userData.h3_index === h3_index;
                                });
                                if (measure) {
                                    window.potreeViewer.scene.removeMeasurement(measure);
                                }
                                // Remove AOI from table
                                let newTableData = tableData.filter((item: any) => {
                                    return item.h3_index !== h3_index;
                                });
                                setTableData(newTableData);
                                message.success("AOI deleted successfully");
                            }).catch((err) => {
                                message.error("Failed to delete AOI");
                                console.log(err);
                            }).finally(() => {
                                setDataLoading(false);
                            });
                        };
                        const handleAoiReload = () => {
                            updateZValueForAOI(record)
                        }

                        return (
                            <div className="actionIconBox">
                                <Tooltip title="Process">
                                    <ExportIcon key="ExportIcon" title="Export" color='#ffffff' onClick={() => onActionHandler(AOIIndexActionType.OpenProcessingModal, DefaultValue, record)} />
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <Popconfirm
                                        placement="topRight"
                                        title="Are you sure to delete this item?"
                                        onConfirm={handleDelete}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <DeleteIcon key="DeleteIcon" title="Delete" color='#ffffff' />
                                    </Popconfirm>
                                </Tooltip>
                                <Tooltip title="Z-Resolving">
                                    <ReloadIcon key="ReloadIcon" title="Reload" color='#ffffff' onClick={handleAoiReload}/>
                                </Tooltip>

                            </div>
                        );
                    }
                }
            } else {
                let data = {
                    accessorKey: key,
                    header: title || "",
                    size: size,
                }
                return data
            };

        })
    }, [tableData]);

    const table = useMaterialReactTable({
        autoResetPageIndex: false,
        columns,
        data: tableData,
        enableSorting: false,
        positionToolbarAlertBanner: 'none',
        rowNumberDisplayMode: "static",
        initialState: {
            showGlobalFilter: true,
            pagination: {
                pageSize: 10,
                pageIndex: 0
            },
        },
        renderBottomToolbarCustomActions: ({ table }) => (
            <div className='create_batch_block'>
                <Button className='batch_btn' loading={deleteLoading} disabled={selectedAoi.length > 0 ? false : true} onClick={handleDeleteAoi}>
                    Delete Aoi
                </Button>
            </div>
        ),
    });

    if (!table) {
        return null;
    }

    const tabItems = [
        {
            label: (
                <span>
                    AOIs
                </span>
            ),
            key: '1',
            children: (
                <div className="inputData_table_searchable dataIndex_table">
                    <div className='loading_box'>
                        <Spin spinning={dataLoading}>
                            {tableData && tableData.length > 0 && (
                                <div ref={tableRef} className={`materialReactTableCustom material_menu_table aois_table ${expand ? 'expand' : ''}`}>
                                    <MaterialReactTable table={table} />
                                </div>
                            )}
                        </Spin>
                    
                    </div>
                </div>
            ),
        },
    ];



    function closeCardBox(value: any) {
        setActiveMenu(null)
    }

    function expandTable(value: any) {
        setExpand(!expand)
    }

    const onActionHandler = async (actionType: AOIIndexActionType, value: any, record: DataAOI) => {
        const dataItem = getAOIDataFromH3Index(tableData, record.h3_index);
        if (!dataItem) return;

        if (actionType === AOIIndexActionType.ZoomTo) {
            zoomTOAOI(record)

            // Update table data
            record.visible = true
            let newTableData = [...tableData];
            setTableData(newTableData);
        }
        if (actionType === AOIIndexActionType.OpenModal) {
            setModalVisible(true)
            // setActiveRecord(record)
        }
        if (actionType === AOIIndexActionType.OpenProcessingModal) {
            setActiveRecord(record)
            setMenuModalVisible(true)
        }
        if (actionType === AOIIndexActionType.Show) {
            dataItem.visible = !dataItem.visible;
            let newTableData = [...tableData];
            setTableData(newTableData);
        }
        if (actionType === AOIIndexActionType.Hide) {
            dataItem.visible = !dataItem.visible;
            let newTableData = [...tableData];
            setTableData(newTableData);
        }
    };

    const closeDataModal = () => {
        setModalVisible(false);
    };

    const closeMenuModal = () => {
        setMenuModalVisible(false);
        setMenuAoisOpen(false)
    };

    return (
        <>
            {
                visible &&
                <>
                    <DataIndexMenuTableContainer>
                        <div className='table-container'>
                            <div className="custom-tab-container">
                                <Tabs defaultActiveKey="1"
                                    items={tabItems}
                                    key="1"
                                    className="custom-tabs">
                                </Tabs>
                                <div className="tab-icons">
                                        <ReloadOutlined key="reload" title="Reload" onClick={() => onReload()} style={{ color : "#ffffff"}} />
                                    {expand ?
                                        <ShrinkIcon key="ShrinkIcon" title="Shrink" color='#ffffff' onClick={expandTable} style={{ marginLeft: 8 }}/>
                                        :
                                        <ExpandIcon key="ExpandIcon" title="Expand" color='#ffffff' onClick={expandTable} style={{ marginLeft: 8 }}/>
                                    }
                                    <CloseIcon key="CloseIcon" title="Close" color='#ffffff' style={{ marginLeft: 8 }} onClick={closeCardBox} />
                                </div>
                            </div>
                        </div>
                    </DataIndexMenuTableContainer>
                    {modalVisible && activeRecord && (
                        <AoisLasExport
                            visible={modalVisible}
                            onClose={closeDataModal}
                            record={activeRecord}
                        />
                    )}

                    {menuModalVisible && (
                        <AoisProcessingMenu
                            setModalVisible={setModalVisible}
                            menuModalVisible={menuModalVisible}
                            onClose={closeMenuModal}
                            record={activeRecord}
                            setActiveRecord={setActiveRecord}
                            tableData={tableData}
                        />
                    )}
                </>
            }
        </>
    );
}

export default AoisMenuTable;
